import axios from 'services/interceptor/Interceptor';
import Config from 'services/config/config';

export const PART_SIZE_BYTES = 8388608;
export const MAX_ZIP_FILE_BYTES = 250 * 1024 * 1024;
export const MAX_CSV_FILE_BYTES = 50 * 1024 * 1024;
export const FASTAG_SERVICE_NAME = 'fastag';
export const ZIP_CONTENT_TYPE = 'application/zip';
export const CSV_CONTENT_TYPE = 'text/csv';

export const FASTAG_BULK_REQUEST_TYPES = {
  KYV: 'KYV',
  VRN_DETAIL: 'VRN_DETAIL',
  CHASSIS_TO_VRN: 'CHASSIS_TO_VRN',
  RC_UPLOAD: 'RC_IMAGE',
};

const CSV_FILE_CONFIG = {
  extension: 'csv',
  contentType: CSV_CONTENT_TYPE,
  maxBytes: MAX_CSV_FILE_BYTES,
  allowedTypes: [
    CSV_CONTENT_TYPE,
    'application/vnd.ms-excel',
    'application/csv',
    'text/plain',
  ],
};

const BULK_FILE_CONFIG = {
  [FASTAG_BULK_REQUEST_TYPES.KYV]: {
    extension: 'zip',
    contentType: ZIP_CONTENT_TYPE,
    maxBytes: MAX_ZIP_FILE_BYTES,
    allowedTypes: [ZIP_CONTENT_TYPE, 'application/x-zip-compressed'],
  },
  [FASTAG_BULK_REQUEST_TYPES.VRN_DETAIL]: CSV_FILE_CONFIG,
  [FASTAG_BULK_REQUEST_TYPES.CHASSIS_TO_VRN]: CSV_FILE_CONFIG,
  [FASTAG_BULK_REQUEST_TYPES.RC_UPLOAD]: CSV_FILE_CONFIG,
};

export function formatFileSize(bytes) {
  if (!bytes || bytes < 1) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${size.toFixed(unitIndex === 0 ? 0 : 2)} ${units[unitIndex]}`;
}

export function getPartCount(fileSize) {
  return Math.ceil(fileSize / PART_SIZE_BYTES);
}

export function validateZipFile(file) {
  return validateFastagBulkFile(file, FASTAG_BULK_REQUEST_TYPES.KYV);
}

export function validateCsvFile(file) {
  return validateFastagBulkFile(file, FASTAG_BULK_REQUEST_TYPES.VRN_DETAIL);
}

export function validateFastagBulkFile(file, requestType = FASTAG_BULK_REQUEST_TYPES.KYV) {
  const normalizedRequestType = BULK_FILE_CONFIG[requestType]
    ? requestType
    : FASTAG_BULK_REQUEST_TYPES.KYV;
  const config = BULK_FILE_CONFIG[normalizedRequestType];
  const extension = config.extension;
  const maxLabel = formatFileSize(config.maxBytes);

  if (!file) {
    return `Please select a ${extension} file.`;
  }

  const name = (file.name || '').toLowerCase();
  if (!name.endsWith(`.${extension}`)) {
    return `Only .${extension} files are allowed.`;
  }

  if (
    normalizedRequestType === FASTAG_BULK_REQUEST_TYPES.KYV &&
    file.type &&
    !config.allowedTypes.includes(file.type)
  ) {
    return `File must be a ${extension} file (${config.contentType}).`;
  }

  if (file.size < 1) {
    return 'File is empty.';
  }

  if (file.size > config.maxBytes) {
    return `File size exceeds the maximum allowed ${maxLabel}.`;
  }

  return null;
}

function extractErrorMessage(data) {
  if (!data) return 'Upload failed';
  if (typeof data.message === 'string' && data.message) return data.message;
  if (Array.isArray(data.message) && data.message.length) {
    return data.message.join(', ');
  }

  const walk = (obj) => {
    if (!obj || typeof obj !== 'object') return null;
    if (Array.isArray(obj.message) && obj.message.length) {
      return obj.message.join(', ');
    }
    return Object.keys(obj).reduce((found, key) => found || walk(obj[key]), null);
  };

  return walk(data) || 'Upload failed';
}

async function createUploadSession(file, requestType = FASTAG_BULK_REQUEST_TYPES.KYV) {
  const config = BULK_FILE_CONFIG[requestType] || BULK_FILE_CONFIG[FASTAG_BULK_REQUEST_TYPES.KYV];

  const payload = {
    service_name: FASTAG_SERVICE_NAME,
    doc_data: [
      {
        doc_name: file.name,
        doc_extension: config.extension,
        content_type: config.contentType,
        file_size: file.size,
      },
    ],
  };

  try {
    const response = await axios.post(Config.fastag.presignedUrlCreate, payload, {
      headers: { 'Content-Type': 'application/json' },
    });

    const body = response?.data;
    if (!body || body.status !== 0) {
      throw new Error(extractErrorMessage(body));
    }

    const session = body.data?.[0];
    if (!session?.upload_id || !session?.parts?.length) {
      throw new Error('Invalid upload session response from server.');
    }

    return session;
  } catch (error) {
    if (error?.response?.data) {
      throw new Error(extractErrorMessage(error.response.data));
    }
    throw error;
  }
}

async function uploadPart(uploadUrl, blob) {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/octet-stream' },
    body: blob,
  });

  if (!response.ok) {
    throw new Error(`Failed to upload part to storage (HTTP ${response.status}).`);
  }

  const etag = response.headers.get('ETag') || response.headers.get('etag');
  if (!etag) {
    throw new Error('Storage did not return an ETag for the uploaded part.');
  }

  return etag.replace(/^"|"$/g, '');
}

async function completeUpload(uploadId, parts) {
  try {
    const response = await axios.post(
      Config.fastag.presignedUrlComplete,
      { upload_id: uploadId, parts },
      { headers: { 'Content-Type': 'application/json' } }
    );

    const body = response?.data;
    if (!body || body.status !== 0) {
      throw new Error(extractErrorMessage(body));
    }

    return body.data;
  } catch (error) {
    if (error?.response?.data) {
      throw new Error(extractErrorMessage(error.response.data));
    }
    throw error;
  }
}

/**
 * Upload a zip file to Linode via Document Router presigned multipart API.
 * @param {File} file
 * @param {(progress: { phase: string, percent: number, currentPart?: number, totalParts?: number }) => void} [onProgress]
 * @returns {Promise<{ cdn_url: string, object_key: string, upload_id: string, status: string }>}
 */
export async function uploadFastagBulkFile(file, onProgress) {
  const validationError = validateZipFile(file);
  if (validationError) {
    throw new Error(validationError);
  }

  onProgress?.({ phase: 'creating', percent: 0 });

  const session = await createUploadSession(file, FASTAG_BULK_REQUEST_TYPES.KYV);
  const { upload_id: uploadId, parts, part_size_bytes: partSize, total_parts: totalParts } =
    session;

  const uploadedParts = [];
  const effectivePartSize = partSize || PART_SIZE_BYTES;

  for (let index = 0; index < parts.length; index += 1) {
    const part = parts[index];
    const partNumber = part.part_number;
    const start = (partNumber - 1) * effectivePartSize;
    const end = Math.min(start + effectivePartSize, file.size);
    const blob = file.slice(start, end);

    const etag = await uploadPart(part.upload_url, blob);
    uploadedParts.push({ part_number: partNumber, etag });

    const uploadPercent = Math.round(((index + 1) / totalParts) * 85);
    onProgress?.({
      phase: 'uploading',
      percent: uploadPercent,
      currentPart: partNumber,
      totalParts,
    });
  }

  onProgress?.({ phase: 'completing', percent: 90 });

  const result = await completeUpload(uploadId, uploadedParts);

  onProgress?.({ phase: 'done', percent: 100 });

  return result;
}

/**
 * Upload a zip file to Linode via Document Router presigned multipart API.
 * @param {File} file
 * @param {(progress: { phase: string, percent: number, currentPart?: number, totalParts?: number }) => void} [onProgress]
 * @returns {Promise<{ cdn_url: string, object_key: string, upload_id: string, status: string }>}
 */
export async function uploadFastagZipFile(file, onProgress) {
  return uploadFastagBulkFile(file, onProgress);
}

/**
 * Submit uploaded FASTag bulk file to bulk-upload API.
 * @param {string} fileLink CDN URL from presigned upload complete response
 * @param {string} [requestType]
 * @returns {Promise<Record<string, unknown>>}
 */
export async function submitFastagBulkUpload(
  fileLink,
  requestType = FASTAG_BULK_REQUEST_TYPES.KYV
) {
  if (!fileLink) {
    throw new Error('Missing file link for bulk upload.');
  }

  const normalizedRequestType = BULK_FILE_CONFIG[requestType]
    ? requestType
    : FASTAG_BULK_REQUEST_TYPES.KYV;

  try {
    const response = await axios.post(
      Config.fastag.bulkUploadKyV,
      {
        request_type: normalizedRequestType,
        file_link: fileLink,
      },
      { headers: { 'Content-Type': 'application/json' } }
    );

    const body = response?.data;
    if (!body || body.status !== 0) {
      throw new Error(extractErrorMessage(body));
    }

    return body.data;
  } catch (error) {
    if (error?.response?.data) {
      throw new Error(extractErrorMessage(error.response.data));
    }
    throw error;
  }
}

/**
 * Validate FASTag bulk upload request before submit (RC_UPLOAD flow).
 * @param {string} fileLink
 * @param {string} [action]
 * @returns {Promise<Record<string, unknown>>}
 */
export async function validateFastagBulkUploadRequest(
  fileLink,
  action = 'validate_rc_image_prices'
) {
  if (!fileLink) {
    throw new Error('Missing file link for validation.');
  }

  try {
    const response = await axios.post(
      Config.fastag.validateBulkUploadRequest,
      {
        action,
        file_link: fileLink,
      },
      { headers: { 'Content-Type': 'application/json' } }
    );

    const body = response?.data;
    if (!body || body.status !== 0) {
      throw new Error(extractErrorMessage(body));
    }

    return body;
  } catch (error) {
    if (error?.response?.data) {
      throw new Error(extractErrorMessage(error.response.data));
    }
    throw error;
  }
}

/**
 * Submit uploaded Re-KYV zip to FASTag bulk-upload API.
 * @param {string} fileLink CDN URL from presigned upload complete response
 * @param {string} [requestType]
 * @returns {Promise<Record<string, unknown>>}
 */
export async function submitFastagReKyvBulkUpload(
  fileLink,
  requestType = FASTAG_BULK_REQUEST_TYPES.KYV
) {
  return submitFastagBulkUpload(fileLink, requestType);
}

const LISTING_PAGE_SIZE = 10;

export const EMPTY_REKYV_LISTING_FILTERS = {
  request_status: '',
  created_date_from: '',
  created_date_to: '',
  request_id: '',
  requestor_name: '',
};

const isValidDateValue = (value) =>
  value && String(value).toLowerCase() !== 'invalid date';

const toApiDate = (dateStr) => {
  if (!isValidDateValue(dateStr)) return '';
  const parts = String(dateStr).split('-');
  if (parts.length === 3 && parts[0].length === 2) {
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  }
  return dateStr;
};

/**
 * Build bulk-upload listing request body with optional filter/search params.
 * @param {{ page?: number, limit?: number, filters?: Record<string, string> }} params
 */
export function buildBulkUploadListingPayload({
  page = 1,
  limit = LISTING_PAGE_SIZE,
  filters = {},
} = {}) {
  const payload = {
    page,
    limit,
    filter: {},
    search: {},
  };

  if (filters.request_status) {
    payload.filter.request_status = filters.request_status;
  }

  const createdFrom = toApiDate(filters.created_date_from);
  const createdTo = toApiDate(filters.created_date_to);
  if (createdFrom) {
    payload.filter.created_date = createdTo
      ? `${createdFrom},${createdTo}`
      : `${createdFrom},${createdFrom}`;
  }

  if (filters.request_id) {
    payload.search.request_id = String(filters.request_id).trim();
  }

  if (filters.requestor_name) {
    payload.search.requestor_name = String(filters.requestor_name).trim();
  }

  if (!Object.keys(payload.filter).length) {
    delete payload.filter;
  }

  if (!Object.keys(payload.search).length) {
    delete payload.search;
  }

  return payload;
}

/**
 * Fetch FASTag bulk-upload listing (Re-KYV history).
 * @param {{ page?: number, limit?: number, filters?: Record<string, string> }} [params]
 * @returns {Promise<{ count: number, page: number, limit: number, results: Array<Record<string, unknown>> }>}
 */
export async function fetchFastagBulkUploadListing({
  page = 1,
  limit = LISTING_PAGE_SIZE,
  filters = {},
} = {}) {
  try {
    const response = await axios.post(
      Config.fastag.bulkUploadListing,
      buildBulkUploadListingPayload({ page, limit, filters }),
      { headers: { 'Content-Type': 'application/json' } }
    );

    const body = response?.data;
    if (!body || body.status !== 0) {
      throw new Error(extractErrorMessage(body));
    }

    return body.data;
  } catch (error) {
    if (error?.response?.data) {
      throw new Error(extractErrorMessage(error.response.data));
    }
    throw error;
  }
}

export { LISTING_PAGE_SIZE };
